import { request } from '@/api';

const PREFIX = '/auth/login';

export const LoginMethod = {
  /**
   * 邮箱密码登录
   */
  EmailPassword: 'emailPassword',
  /**
   * 手机号密码登录
   */
  PhonePassword: 'phonePassword',
  /**
   * 手机号短信验证码登录
   */
  PhoneSmsCode: 'phoneSmsCode',
} as const;

export type LoginMethodType = (typeof LoginMethod)[keyof typeof LoginMethod];

export const AccessMode = {
  /**
   * Creative 系统正常登录
   */
  Normal: 'NORMAL',
  /**
   * Management 系统白名单用户登录 Creative 系统
   */
  ManagementAsCreative: 'MANAGEMENT_AS_CREATIVE',
} as const;

export type AccessModeType = (typeof AccessMode)[keyof typeof AccessMode];

export const MfaTypeValues = {
  /**
   * 短信验证码
   */
  SMS: 'smsCode',
  /**
   * 邮箱验证码
   */
  Email: 'emailCode',
} as const;

export type MfaType = (typeof MfaTypeValues)[keyof typeof MfaTypeValues];

export interface TempAuthVO {
  /**
   * 临时令牌有效时间（单位：秒)
   */
  expireIn: number;
  /**
   * 临时令牌
   */
  tempToken: string;
  /**
   * 授权行为（临时令牌仅在对应行为有效）
   */
  authAction: string;
  /**
   * 额外信息
   */
  extra?: {
    phone?: string;
    email?: string;
    [key: string]: string | undefined;
  };
}

/**
 * /login MFA第一步授权临时token
 * @param method 登录方法
 * @param emailOrPhone 邮箱或手机号
 * @param passwordOrSmsCode 密码或短信验证码
 * @param accessMode 访问模式
 * @example
 * // 邮箱密码登录
 * login(LoginMethod.EmailPassword, 'user@example.com', 'password123');
 *
 * // 手机号密码登录
 * login(LoginMethod.PhonePassword, '12345678901', 'password123');
 *
 * // 手机号短信验证码登录
 * login(LoginMethod.PhoneSmsCode, '12345678901', '123456');
 */
export const login = (
  method: LoginMethodType,
  emailOrPhone: string,
  passwordOrSmsCode: string,
  accessMode: AccessModeType = AccessMode.Normal
) => {
  const payload: Record<string, string> = {
    grantType: method,
    accessMode,
  };
  switch (method) {
    case LoginMethod.EmailPassword:
      payload.email = emailOrPhone;
      payload.password = passwordOrSmsCode;
      break;
    case LoginMethod.PhonePassword:
      payload.phone = emailOrPhone;
      payload.password = passwordOrSmsCode;
      break;
    case LoginMethod.PhoneSmsCode:
      payload.phone = emailOrPhone;
      payload.smsCode = passwordOrSmsCode;
      break;
  }
  return request.post<TempAuthVO>(PREFIX, payload);
};

/**
 * /login/mfa/verify MFA验证(登陆流程第二步)
 * @param mfaType MFA类型
 * @param token 临时令牌
 * @param code MFA验证码
 * @example
 * // 短信验证码登录
 * verifyMfa(MfaTypeValues.SMS, 'tempToken123', '123456');
 *
 * // 邮箱验证码登录
 * verifyMfa(MfaTypeValues.Email, 'tempToken123', '123456');
 */
export const verifyMfa = (mfaType: MfaType, token: string, code: string) => {
  return request.post<TempAuthVO>(`${PREFIX}/mfa/verify`, {
    mfaType,
    token,
    [mfaType]: code,
  });
};

/**
 * /login/sms/code/send
 * 发送登录短信验证码
 * @param phone 手机号
 */
export const sendLoginSmsCode = (phone: string) => {
  return request.post<string>(`${PREFIX}/sms/code/send`, { phone });
};

/**
 * /login/mfa/code/send
 * 发送登录MFA验证码
 * @param mfaType MFA类型
 * @param tempToken 临时令牌
 * @example
 * // 短信验证码
 * sendLoginMfaCode(MfaTypeValues.SMS, 'tempToken123');
 *
 * // 邮箱验证码
 * sendLoginMfaCode(MfaTypeValues.Email, 'tempToken123');
 */
export const sendLoginMfaCode = (mfaType: MfaType, tempToken: string) => {
  return request.post<string>(`${PREFIX}/mfa/code/send`, {
    mfaType,
    tempToken,
  });
};
